import { useState, useEffect } from "react";

/**
 * A custom hook that debounces a value, delaying updates until after a specified delay
 *
 * @example
 * ```tsx
 * function SearchComponent() {
 *   const [searchTerm, setSearchTerm] = useState('')
 *   const debouncedSearchTerm = useDebounce(searchTerm, 300)
 *
 *   useEffect(() => {
 *     if (debouncedSearchTerm) {
 *       // Perform search API call here
 *       performSearch(debouncedSearchTerm)
 *     }
 *   }, [debouncedSearchTerm])
 *
 *   return (
 *     <input
 *       value={searchTerm}
 *       onChange={(e) => setSearchTerm(e.target.value)}
 *       placeholder="Search..."
 *     />
 *   )
 * }
 * ```
 *
 * @param value - The value to debounce
 * @param delay - The delay in milliseconds (default: 300ms for search)
 * @returns The debounced value
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    // Set up a timer to update the debounced value after the delay
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Clean up the timer if value changes before delay completes
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * A custom hook that debounces a callback function
 *
 * @example
 * ```tsx
 * function FormComponent() {
 *   const [formData, setFormData] = useState({ name: '', email: '' })
 *
 *   const saveFormData = useDebouncedCallback((data) => {
 *     // Auto-save form data
 *     saveToLocalStorage(data)
 *   }, 500)
 *
 *   useEffect(() => {
 *     saveFormData(formData)
 *   }, [formData, saveFormData])
 *
 *   return (
 *     <form>
 *       <input
 *         value={formData.name}
 *         onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
 *       />
 *     </form>
 *   )
 * }
 * ```
 *
 * @param callback - The function to debounce
 * @param delay - The delay in milliseconds
 * @returns The debounced callback function
 */
export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
): T {
  const [debouncedCallback, setDebouncedCallback] = useState<T>(() => callback);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCallback(() => callback);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [callback, delay]);

  return debouncedCallback;
}

// Common delay constants for convenience
export const DEBOUNCE_DELAYS = {
  SEARCH: 300, // For search inputs
  AUTO_SAVE: 500, // For auto-saving forms
  RESIZE: 100, // For window resize events
  SCROLL: 50, // For scroll events
  API_CALL: 400, // For API calls
} as const;
